import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { DAY_LABELS } from '../../types/exercise';
import { getTrainingDay } from '../../lib/scheduler';
import { dateKey } from '../../lib/setProgress';
import { useDayCompletedCount } from '../../lib/useSetProgress';
import { useWeekStats, sumWeekTotals } from '../../lib/weekStats';
import { useStreak } from '../../lib/streak';
import { showToast } from '../../lib/toast';
import { isIOS } from '../../lib/platform';
import { requireExerciseById } from '../../data/exercises';
import { ExerciseCard } from '../training/ExerciseCard';
import { ExerciseDetail } from '../training/ExerciseDetail';
import { AuthButton } from '../auth/AuthButton';
import { WeekStrip } from './WeekStrip';
import { HeroCard } from './HeroCard';
import { StatsTiles } from './StatsTiles';

interface DashboardViewProps {
  today: Date;
}

export function DashboardView({ today }: DashboardViewProps): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [openId, setOpenId] = useState<string | null>(null);
  const exerciseListRef = useRef<HTMLElement>(null);

  const todayKey = useMemo(() => dateKey(today), [today]);
  const selectedKey = useMemo(() => dateKey(selectedDate), [selectedDate]);
  const isToday = todayKey === selectedKey;

  const plan = useMemo(() => getTrainingDay(selectedDate), [selectedDate]);

  const weekStats = useWeekStats(today);
  const totals = useMemo(() => sumWeekTotals(weekStats), [weekStats]);
  const streak = useStreak(today);
  const trainingDaysDone = useMemo(
    () =>
      weekStats.filter(
        (s) =>
          s.plan.kind === 'training' &&
          s.totalExercises > 0 &&
          s.completedExercises >= s.totalExercises,
      ).length,
    [weekStats],
  );

  const trainingEntries = plan.kind === 'training' ? plan.entries : [];
  const completed = useDayCompletedCount(selectedKey, trainingEntries);
  const totalExercises = trainingEntries.length;

  // Toast bei „Training abgeschlossen" — nur fuer Heute (Vergangenheit nachtragen toastet nicht).
  const prevCompletedRef = useRef(completed);
  useEffect(() => {
    if (!isToday || totalExercises === 0) {
      prevCompletedRef.current = completed;
      return;
    }
    if (prevCompletedRef.current < totalExercises && completed >= totalExercises) {
      showToast('Training abgeschlossen — gut gemacht!', {
        variant: 'success',
        durationMs: 3000,
      });
    }
    prevCompletedRef.current = completed;
  }, [completed, totalExercises, isToday]);

  // Reset prev-ref bei Tag-Wechsel, damit „Training abgeschlossen" nicht versehentlich feuert.
  useEffect(() => {
    prevCompletedRef.current = completed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const currentIndex =
    plan.kind === 'training' && openId
      ? plan.entries.findIndex((e) => e.exerciseId === openId)
      : -1;
  const openEntry =
    plan.kind === 'training' && currentIndex >= 0 ? plan.entries[currentIndex] : undefined;
  const openExerciseObj = openId ? requireExerciseById(openId) : null;
  const nextEntry =
    plan.kind === 'training' && currentIndex >= 0 ? plan.entries[currentIndex + 1] : undefined;

  const advance = (): void => {
    if (nextEntry) {
      setOpenId(nextEntry.exerciseId);
    } else {
      // Letzte Uebung abgeschlossen → Sheet schliessen + (auf iOS) Apple-Workout beenden
      setOpenId(null);
      if (showAppleButton) {
        triggerAppleWorkoutEnd();
      }
    }
  };

  const onSelectDate = (date: Date): void => {
    setSelectedDate(date);
    setOpenId(null);
  };

  // iOS-only: Apple-Workout-Auto-Sync via Shortcuts.
  // Voraussetzung: Operator hat zwei Kurzbefehle angelegt:
  //   - "NeoOlymp"     startet Funktionelles Krafttraining (siehe CHANGELOG VOE-197)
  //   - "NeoOlympEnd"  beendet das aktive Workout (siehe CHANGELOG VOE-198)
  // Trigger sind bewusst NICHT separate UI-Buttons, sondern in den existierenden
  // Aktionen verschmolzen (VOE-199): "Training starten" startet automatisch
  // das Apple-Workout, der finale "Training abschliessen"-Klick beendet es.
  const showAppleButton = isIOS() && plan.kind === 'training' && trainingEntries.length > 0;
  const triggerAppleWorkoutStart = (): void => {
    window.location.href = 'shortcuts://run-shortcut?name=NeoOlymp';
  };
  const triggerAppleWorkoutEnd = (): void => {
    window.location.href = 'shortcuts://run-shortcut?name=NeoOlympEnd';
  };

  const onStart = (): void => {
    const el = exerciseListRef.current;
    if (!el) return;
    // Window-Scroll mit 16px Offset oben — robuster als scrollIntoView
    // (einige iOS-Safari-Versionen ignorieren scrollIntoView in PWAs).
    const targetTop = el.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    if (showAppleButton) {
      triggerAppleWorkoutStart();
    }
  };

  const greeting = greetingFor(today);
  const dateDisplay = formatDate(selectedDate);

  return (
    <main
      className="min-h-dvh bg-zinc-50 text-zinc-900 font-sans"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <header className="mb-5 flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {greeting}
            </p>
            <h1 className="mt-1 text-2xl font-semibold leading-tight text-zinc-900 md:text-3xl">
              Hi Thomas
            </h1>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <AuthButton />
            <p className="text-sm text-zinc-600">{dateDisplay}</p>
          </div>
        </header>

        <section className="mb-5">
          <WeekStrip
            stats={weekStats}
            todayKey={todayKey}
            selectedKey={selectedKey}
            onSelect={onSelectDate}
          />
        </section>

        {!isToday ? (
          <button
            type="button"
            onClick={() => setSelectedDate(today)}
            className="mb-4 inline-flex h-9 items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-700 active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          >
            <ArrowLeft aria-hidden="true" className="size-3.5" />
            Zurueck zu heute
          </button>
        ) : null}

        <section className="mb-5">
          <HeroCard
            plan={plan}
            isToday={isToday}
            completedExercises={completed}
            totalExercises={totalExercises}
            onStart={plan.kind === 'training' && trainingEntries.length > 0 ? onStart : undefined}
          />
        </section>

        <section className="mb-6">
          <StatsTiles
            streak={streak}
            trainingDaysThisWeek={trainingDaysDone}
            setsThisWeek={totals.totalSetsDone}
          />
        </section>

        {plan.kind === 'training' ? (
          <section ref={exerciseListRef}>
            {plan.circuitNote ? (
              <p className="mb-3 rounded-lg bg-emerald-50 p-3 text-sm leading-normal text-emerald-900">
                {plan.circuitNote}
              </p>
            ) : null}

            <h2 className="mb-3 text-xl font-semibold leading-tight text-zinc-900">Uebungen</h2>
            <ul className="flex flex-col gap-2">
              {plan.entries.map((entry, idx) => {
                const ex = requireExerciseById(entry.exerciseId);
                return (
                  <li key={`${entry.exerciseId}-${idx}`}>
                    <ExerciseCard
                      exercise={ex}
                      entry={entry}
                      dayKey={selectedKey}
                      onOpen={(id) => setOpenId(id)}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {plan.kind === 'recovery' ? (
          <section ref={exerciseListRef} className="rounded-xl border border-zinc-200 bg-white p-5">
            <h2 className="text-base font-semibold text-zinc-900">{DAY_LABELS[plan.dayOfWeek]} — Aktivitaeten</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-base leading-normal text-zinc-900">
              {plan.activities.map((act, idx) => (
                <li key={idx}>{act}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="h-8" />
      </div>

      {openExerciseObj && openEntry ? (
        <ExerciseDetail
          exercise={openExerciseObj}
          entry={openEntry}
          dayKey={selectedKey}
          onClose={() => setOpenId(null)}
          onAdvance={advance}
          hasNext={nextEntry !== undefined}
        />
      ) : null}
    </main>
  );
}

function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Hallo';
  return 'Guten Abend';
}

function formatDate(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${DAY_LABELS[dayOfWeek(date)]}, ${d}.${m}.${date.getFullYear()}`;
}

function dayOfWeek(date: Date): keyof typeof DAY_LABELS {
  const map: Record<number, keyof typeof DAY_LABELS> = {
    0: 'sun',
    1: 'mon',
    2: 'tue',
    3: 'wed',
    4: 'thu',
    5: 'fri',
    6: 'sat',
  };
  return map[date.getDay()] ?? 'mon';
}
